import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Search, X, Menu } from "lucide-react";
import { Screen } from "../types";
import ThemeTogglerButton from "./ui/ThemeTogglerButton";
import { fetchSettings } from "../hooks/useSettings";

interface NavbarProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ isDarkMode, onToggleTheme }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);
  const location = useLocation();
  const navigate = useNavigate();

  const [logoSettings, setLogoSettings] = useState<{ light?: string; dark?: string }>({});

  useEffect(() => {
    fetchSettings().then((data) => {
      setLogoSettings({
        light: data.site_logo_light,
        dark: data.site_logo_dark,
      });
    });
  }, []);

  // Close search on route change
  useEffect(() => {
    setIsSearchOpen(false);
    setSearchQuery("");
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Focus input when search opens
  useEffect(() => {
    if (isSearchOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [isSearchOpen]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = searchQuery.trim();
    if (!q) return;
    navigate(`/?s=${encodeURIComponent(q)}`);
    setIsSearchOpen(false);
    setSearchQuery("");
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    // Initialize state based on current scroll
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { label: "Home", path: "/" },
    { label: "Services", path: "/services" },
    { label: "Academy", path: "/courses" },
    { label: "Programmes", path: "/programmes" },
    { label: "Team", path: "/team" },
    { label: "About Us", path: "/about" },
  ];

  const isActive = (path: string) => location.pathname === path;

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 flex justify-center pointer-events-none transition-[padding] duration-500 ease-[cubic-bezier(0.25,0.8,0.25,1)] ${
        isScrolled ? "px-4 pt-4" : "px-0 pt-0"
      }`}
    >
      <nav
        className={`pointer-events-auto relative z-50 flex items-center justify-between transition-[max-width,border-radius,padding,border-color,background-color,shadow] duration-500 ease-[cubic-bezier(0.25,0.8,0.25,1)] bg-surface/80 dark:bg-surface-dark/80 backdrop-blur-md shadow-sm w-full border ${
          isScrolled
            ? "max-w-[1120px] rounded-2xl border-border-subtle dark:border-white/10 px-6 py-3 md:px-8 md:py-4"
            : "max-w-full rounded-none border-x-transparent border-t-transparent border-b-border-subtle dark:border-b-white/10 px-6 py-5 md:px-10 md:py-6"
        }`}
      >
        <Link
          to="/"
          className="flex items-center gap-3 cursor-pointer group"
          onClick={() => setIsMenuOpen(false)}
        >
          <div className="h-10 w-auto flex items-center justify-center transition-transform">
            <img
              src={
                isDarkMode
                  ? (logoSettings.dark || "/logo/darkmode-logo.webp")
                  : (logoSettings.light || "/logo/lightmode-logo.webp")
              }
              alt="InnovaTech Logo"
              width="175"
              height="40"
              className="h-full w-auto object-contain"
            />
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.path}
              className={`text-sm font-medium transition-colors hover:text-text-main dark:hover:text-white ${
                isActive(link.path)
                  ? "text-primary"
                  : "text-text-sub dark:text-gray-400"
              }`}
            >
              {link.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Search — desktop inline expanding */}
          <div className="hidden md:flex items-center">
            <form
              onSubmit={handleSearch}
              className={`flex items-center gap-2 overflow-hidden transition-all duration-300 ease-in-out ${
                isSearchOpen
                  ? "w-48 opacity-100"
                  : "w-0 opacity-0 pointer-events-none"
              }`}
            >
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Escape" && setIsSearchOpen(false)}
                placeholder="Search pages..."
                className="w-full text-sm bg-gray-100 dark:bg-white/5 border border-border-subtle dark:border-white/10 rounded-xl px-3 py-2 text-text-main dark:text-white placeholder-text-sub dark:placeholder-gray-500 outline-none focus:ring-2 focus:ring-primary/40"
              />
            </form>
            <button
              onClick={() => setIsSearchOpen((v) => !v)}
              className="relative size-10 rounded-full flex items-center justify-center text-text-sub dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
              aria-label="Search"
            >
              {isSearchOpen ? <X className="w-5 h-5" /> : <Search className="w-5 h-5" />}
            </button>
          </div>

          {/* Theme Toggler */}
          <ThemeTogglerButton
            variant="ghost"
            size="sm"
            onImmediateChange={onToggleTheme}
            className="text-text-sub dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors"
          />

          {/* Desktop Contact Button */}
          <Link
            to="/contact"
            className="hidden md:flex bg-primary hover:bg-red-600 text-white text-sm font-semibold px-6 py-3 rounded-xl transition-all shadow-lg shadow-primary/20 active:scale-95 items-center gap-2"
          >
            <span>Contact Us</span>
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden relative size-10 rounded-full flex items-center justify-center text-text-main dark:text-white hover:bg-gray-100 dark:hover:bg-white/5 transition-colors z-50"
            aria-label="Toggle Menu"
          >
            {isMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`pointer-events-auto fixed inset-0 bg-surface dark:bg-surface-dark/95 backdrop-blur-xl z-40 transition-transform duration-500 ease-in-out md:hidden flex flex-col items-center justify-center gap-8 ${
          isMenuOpen ? "translate-y-0" : "-translate-y-full"
        }`}
      >
        <div className="flex flex-col items-center gap-6 w-full px-8">
          {/* Mobile Search */}
          <form
            onSubmit={handleSearch}
            className="w-full flex items-center gap-2"
          >
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search pages..."
              className="flex-1 text-base bg-gray-100 dark:bg-white/5 border border-border-subtle dark:border-white/10 rounded-xl px-4 py-3 text-text-main dark:text-white placeholder-text-sub dark:placeholder-gray-500 outline-none focus:ring-2 focus:ring-primary/40"
            />
            <button
              type="submit"
              className="size-12 rounded-xl bg-primary text-white flex items-center justify-center shrink-0"
            >
              <Search className="w-5 h-5" />
            </button>
          </form>

          {navLinks.map((link) => (
            <Link
              key={link.label}
              to={link.path}
              onClick={() => setIsMenuOpen(false)}
              className={`text-2xl font-bold transition-colors ${
                isActive(link.path)
                  ? "text-primary"
                  : "text-text-main dark:text-white"
              }`}
            >
              {link.label}
            </Link>
          ))}
          <Link
            to="/contact"
            onClick={() => setIsMenuOpen(false)}
            className="mt-4 bg-primary hover:bg-red-600 text-white text-lg font-semibold px-8 py-4 rounded-xl transition-all shadow-lg shadow-primary/20 active:scale-95 flex items-center gap-2"
          >
            <span>Contact Us</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Navbar;
